import { Content } from '@radix-ui/react-dialog';
import { cva,VariantProps } from 'class-variance-authority';
import React, { useCallback, useEffect, useState } from 'react';

import { DiscardChangesConfirmationModal } from '@/components/DiscardChangesConfirmationModal';

import { DialogOverlay } from './DialogOverlay';
import { DialogPortal } from './DialogPortal';

const dialogVariants = cva(`
    tw-outline-none
    dialog-content
    tw-fixed
    tw-z-50
    tw-flex
    tw-flex-col
    tw-border
    tw-bg-background
    tw-duration-200
    data-[state=open]:tw-animate-in
    data-[state=closed]:tw-animate-out
    data-[state=closed]:tw-fade-out-0
    data-[state=open]:tw-fade-in-0
    data-[state=closed]:tw-zoom-out-95
    data-[state=open]:tw-zoom-in-95`, {
    variants: {
        variant: {
            screen: `tw-bottom-0
                     tw-left-0
                     tw-w-full
                     tw-h-[90vh]
                     tw-rounded-t-lg
                     tw-bg-white
                     data-[state=open]:tw-slide-in-from-bottom
                     data-[state=closed]:tw-slide-out-to-bottom
                     lg:tw-bottom-auto
                     lg:tw-left-1/2
                     lg:tw-top-1/2
                     lg:-tw-translate-x-1/2
                     lg:-tw-translate-y-1/2
                     lg:tw-w-[85vw]
                     lg:tw-max-w-screen-2xl
                     lg:tw-h-[90vh]
                     lg:tw-rounded-lg
                     lg:tw-shadow-xl
                     lg:data-[state=open]:tw-slide-in-from-top-[48%]
                     lg:data-[state=closed]:tw-slide-out-to-top-[48%]
                     lg:data-[state=open]:tw-slide-in-from-left-1/2
                     lg:data-[state=closed]:tw-slide-out-to-left-1/2`,
            large: `tw-bottom-0
                    tw-left-0
                    tw-w-full
                    tw-h-auto
                    tw-rounded-t-lg
                    data-[state=open]:tw-slide-in-from-bottom
                    data-[state=closed]:tw-slide-out-to-bottom
                    lg:tw-bottom-auto
                    lg:tw-left-1/2
                    lg:tw-top-1/2
                    lg:-tw-translate-x-1/2
                    lg:-tw-translate-y-1/2
                    lg:tw-w-[75vw]
                    lg:tw-max-w-6xl
                    lg:tw-h-auto
                    lg:tw-rounded-lg
                    lg:tw-shadow-xl
                    lg:data-[state=open]:tw-slide-in-from-top-[48%]
                    lg:data-[state=closed]:tw-slide-out-to-top-[48%]
                    lg:data-[state=open]:tw-slide-in-from-left-1/2
                    lg:data-[state=closed]:tw-slide-out-to-left-1/2`,
            small: `tw-top-1/2
                    tw-left-1/2
                    -tw-translate-x-1/2
                    -tw-translate-y-1/2
                    tw-w-[calc(100vw-40px)]
                    tw-max-w-xl
                    tw-h-auto
                    tw-rounded-lg
                    tw-shadow-xl
                    data-[state=open]:tw-slide-in-from-top-[48%]
                    data-[state=closed]:tw-slide-out-to-top-[48%]
                    data-[state=open]:tw-slide-in-from-left-1/2
                    data-[state=closed]:tw-slide-out-to-left-1/2`,
        },
    },
    defaultVariants: {
        variant: 'large',
    },
});

const DialogContent = React.forwardRef<
    React.ElementRef<typeof Content>,
    React.ComponentPropsWithoutRef<typeof Content> &
        VariantProps<typeof dialogVariants> &
        {
            useReactPortal?: boolean,
            onEscapeKeyDown?: (event: KeyboardEvent) => void,
            onDiscardChanges?: () => void,
            isDirty?: boolean,
            discardChangesTitle?: string,
            showDiscardModalExternal?: boolean,
            onDiscardModalChange?: (show: boolean) => void,
                }
                >(({ className, children, variant, useReactPortal = true,
                    onEscapeKeyDown, onDiscardChanges, isDirty = false, discardChangesTitle, showDiscardModalExternal, onDiscardModalChange, ...props }, ref) => {
                    const [showDiscardModal, setShowDiscardModal] = useState(false);

                    const isModalControlled = showDiscardModalExternal !== undefined;
                    const modalShow = isModalControlled ? showDiscardModalExternal : showDiscardModal;

                    const setModalShow = useCallback((show: boolean) => {
                        if (isModalControlled && onDiscardModalChange) {
                            onDiscardModalChange(show);
                        } else {
                            setShowDiscardModal(show);
                        }
                    }, [isModalControlled, onDiscardModalChange]);

                    useEffect(() => {
                        // Disable Radix ui dialog pointer events lockout
                        // https://github.com/radix-ui/primitives/issues/1859
                        setTimeout(() => (document.body.style.pointerEvents = ''), 2000);
                    }, []);

                    const handleEscapeKeyDown = useCallback((event: KeyboardEvent) => {
                        if (onEscapeKeyDown) {
                            onEscapeKeyDown(event);
                            return;
                        }

                        if (isDirty && onDiscardChanges && !modalShow) {
                            event.preventDefault();
                            setModalShow(true);
                        }
                    }, [onEscapeKeyDown, isDirty, onDiscardChanges, modalShow, setModalShow]);

                    const handleDiscard = useCallback(() => {
                        setModalShow(false);
                        setTimeout(() => {
                            if (onDiscardChanges) {
                                onDiscardChanges();
                            }
                        }, 100);
                    }, [onDiscardChanges, setModalShow]);

                    const handleCloseDiscardModal = useCallback(() => {
                        setModalShow(false);
                    }, [setModalShow]);

                    return (
                        <DialogPortal useReactPortal={useReactPortal}>
                            {/*
              * Keep `Content` a child of `Overlay` to enable scrolling of child components.
              * See more: https://github.com/radix-ui/primitives/issues/1159
              *           https://github.com/radix-ui/primitives/pull/2250
             */}
                            <DialogOverlay>
                                <Content
                                    ref={ref}
                                    className={dialogVariants({ variant, className })}
                                    onEscapeKeyDown={handleEscapeKeyDown}
                                    {...props}
                                >
                                    {children}
                                    {onDiscardChanges && (
                                        <DiscardChangesConfirmationModal
                                            show={modalShow}
                                            onDiscard={handleDiscard}
                                            onClose={handleCloseDiscardModal}
                                            title={discardChangesTitle}
                                        />
                                    )}
                                </Content>
                            </DialogOverlay>
                        </DialogPortal>
                    );
                });

DialogContent.displayName = Content.displayName;

export { DialogContent };
